// apps/landing-page/src/components/search/SearchResults.tsx
// The result list for the navbar combobox and the dialog: error, skeleton, empty state
// and the grouped listbox. Which row is active is owned by the search box.
'use client';

import { createElement } from 'react';
import Link from 'next/link';
import { CalendarClock, Newspaper, RefreshCw, Search, SearchX } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { examIcon } from '@/lib/exam-icons';
import { EmptyState } from '@/components/ui';
import { GROUP_ORDER, type SearchRow } from './searchRows';
import type { CatalogSearch } from './useCatalogSearch';
import type { SearchBox } from './useSearchBox';

const SKELETON_ROWS = 4;

/** Icon for a row: its exam family icon, else one per group. Not JSX (`<Icon />`): the
 *  component is picked per row, which the static-components lint rule rejects. */
export function rowIcon(row: Pick<SearchRow, 'familySlug' | 'group'>, className: string) {
  const Icon = row.familySlug ? examIcon(row.familySlug)
    : row.group === 'events' ? CalendarClock
    : row.group === 'all' ? Search
    : Newspaper;
  return createElement(Icon, { 'aria-hidden': true, className });
}

/** True when the list would render something, so the combobox knows to show a popover. */
export function hasSearchContent(search: CatalogSearch, box: SearchBox): boolean {
  return search.status === 'error' || box.rows.length > 0 || box.trimmed !== '';
}

interface SearchResultsProps {
  search: CatalogSearch;
  box: SearchBox;
  listId: string;
  /** Called after a plain click on a result or the empty-state link. */
  onSelect: () => void;
}

export function SearchResults({ search, box, listId, onSelect }: SearchResultsProps) {
  const t = useTranslations('search');
  const { rows, trimmed } = box;
  const isPending = search.status === 'loading' || search.status === 'idle';
  const showEmpty = trimmed !== '' && search.status === 'ready' && rows.length === 0;
  const showSkeleton = trimmed !== '' && isPending;
  const seeAll = rows.find((r) => r.group === 'all');

  return (
    <>
      <p role="status" className="sr-only">
        {trimmed && search.status === 'ready' ? t('resultCount', { count: seeAll ? rows.length - 1 : 0 }) : ''}
      </p>

      {search.status === 'error' && (
        <div role="alert" className="m-2 flex flex-col items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">{t('errorTitle')}</p>
          <p className="text-xs text-red-700">{t('errorDesc')}</p>
          <button
            type="button"
            onClick={search.retry}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 text-sm font-semibold text-red-800 transition-colors hover:bg-red-100"
          >
            <RefreshCw className="h-4 w-4" />
            {t('retry')}
          </button>
        </div>
      )}

      {showSkeleton && (
        <div aria-busy="true" aria-label={t('loading')} className="space-y-2 p-2">
          {Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <div key={i} className="flex animate-pulse items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 w-3/4 rounded bg-slate-200" />
                <div className="h-3 w-1/2 rounded bg-slate-200" />
              </div>
            </div>
          ))}
        </div>
      )}

      {showEmpty && (
        <EmptyState
          icon={<SearchX className="h-10 w-10" />}
          title={t('emptyTitle', { query: trimmed })}
          description={t('emptyDesc')}
          action={
            <Link
              href="/tests"
              onClick={onSelect}
              className="inline-flex h-10 items-center rounded-lg border border-teal-600 px-4 text-sm font-semibold text-teal-600 transition-colors hover:bg-teal-50"
            >
              {t('emptyAction')}
            </Link>
          }
        />
      )}

      {rows.length > 0 && (
        <div id={listId} role="listbox" aria-label={t('listLabel')}>
          {GROUP_ORDER.map((group) => {
            const groupRows = rows.filter((r) => r.group === group);
            if (groupRows.length === 0) return null;
            const headingId = `${listId}-${group}`;
            return (
              <div key={group} role="group" aria-labelledby={headingId} className="mb-1">
                <div id={headingId} className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t(`group.${group}`)}
                </div>
                {groupRows.map((row) => (
                  <ResultRow key={row.id} row={row} listId={listId} box={box} search={search} onSelect={onSelect} />
                ))}
              </div>
            );
          })}
          {seeAll && (
            <div role="group" aria-label={t('seeAllGroup')} className="mt-1 border-t border-slate-100 pt-1">
              <ResultRow row={seeAll} listId={listId} box={box} search={search} onSelect={onSelect} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

function ResultRow({ row, listId, box, search, onSelect }: {
  row: SearchRow;
  listId: string;
  box: SearchBox;
  search: CatalogSearch;
  onSelect: () => void;
}) {
  const domId = `${listId}-${row.id}`;
  const isActive = domId === box.activeDomId;
  const color = row.familySlug ? search.colorBySlug.get(row.familySlug) ?? null : null;

  return (
    <Link
      id={domId}
      role="option"
      aria-selected={isActive}
      tabIndex={-1}
      href={row.href}
      // Plain click: the Link navigates itself, we only close. Modified clicks open a new tab and keep the list.
      onClick={(e) => { if (!(e.metaKey || e.ctrlKey || e.shiftKey)) onSelect(); }}
      onMouseMove={() => box.setActiveIndex(box.rows.indexOf(row))}
      className={`flex min-h-[48px] items-center gap-3 rounded-xl px-3 py-2 transition-colors ${isActive ? 'bg-teal-50' : ''}`}
    >
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50" style={color ? { color } : undefined}>
        {rowIcon(row, `h-5 w-5 ${color ? '' : 'text-slate-500'}`)}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[15px] font-semibold text-slate-900">{row.title}</span>
        {row.context && <span className="block truncate text-[13px] text-slate-500">{row.context}</span>}
      </span>
    </Link>
  );
}
